import React, { FC } from 'react';
import {
	Icon,
	IconButton,
	Paper,
	Switch,
	TextField,
	Tooltip,
	Typography,
} from '@mui/material';
import styled from 'styled-components';
import EdButton from '@Components/UI/Buttons/EdButton/EdButton';
import { FlexLayout } from '@Styled/utilities';
import { MaskedCredentials, MaskedSecret } from '../Types/VideoProviders.types';

/* ------------------------------------------------------------------ */
/* Write-only secret handling                                          */
/* ------------------------------------------------------------------ */

export type SecretDraft = {
	/** Typed replacement value ('' = untouched). */
	value: string;
	/** Explicit clear requested — sends null on save. */
	cleared: boolean;
};

export const emptySecretDraft = (): SecretDraft => ({
	value: '',
	cleared: false,
});

export const storedSecret = (
	credentials: MaskedCredentials | undefined,
	key: string
): MaskedSecret | undefined => {
	const stored = credentials?.[key];
	return typeof stored === 'object' ? stored : undefined;
};

/**
 * Builds the PUT credentials patch: cleared keys -> null, typed keys -> the
 * value; untouched keys are omitted so the backend keeps the stored secret
 * (it also treats '' as clear, so blanks must never be sent).
 */
export const buildCredentialsPatch = (
	drafts: Record<string, SecretDraft>,
	plain: Record<string, string | null> = {}
): Record<string, string | null> | undefined => {
	const patch: Record<string, string | null> = { ...plain };
	Object.entries(drafts).forEach(([key, draft]) => {
		if (draft.cleared) patch[key] = null;
		else if (draft.value.trim()) patch[key] = draft.value.trim();
	});
	return Object.keys(patch).length ? patch : undefined;
};

type SecretFieldProps = {
	label: string;
	stored?: MaskedSecret;
	draft: SecretDraft;
	onChange: (draft: SecretDraft) => void;
	helperText?: string;
};

/**
 * Password input for a write-only secret: the placeholder shows the stored
 * hint (••••1234), leaving it blank keeps the stored value, and the side
 * button toggles an explicit clear (null on save).
 */
export const SecretField: FC<SecretFieldProps> = ({
	label,
	stored,
	draft,
	onChange,
	helperText,
}) => {
	const hasStored = !!stored?.has_value;
	const placeholder = draft.cleared
		? 'Will be cleared on save'
		: hasStored
		? stored?.hint ?? '••••'
		: 'Not set';
	const helper = draft.cleared
		? 'Stored value will be removed when you save'
		: helperText ??
		  (hasStored ? 'Leave blank to keep the stored value' : undefined);

	return (
		<FlexLayout alignItems="flex-start" gridGap="0.25rem" width="100%">
			<TextField
				fullWidth
				size="small"
				type="password"
				autoComplete="new-password"
				label={label}
				value={draft.value}
				placeholder={placeholder}
				// Keep the label floated: an unfocused empty MUI field renders the
				// label ON TOP of the placeholder, hiding the stored ••••1234 hint.
				InputLabelProps={{ shrink: true }}
				helperText={helper}
				onChange={(event) =>
					onChange({ value: event.target.value, cleared: false })
				}
			/>
			{hasStored && (
				<Tooltip
					title={draft.cleared ? 'Keep the stored value' : 'Clear stored value'}
				>
					<IconButton
						size="small"
						color={draft.cleared ? 'error' : 'default'}
						onClick={() =>
							onChange({ value: '', cleared: !draft.cleared })
						}
						sx={{ marginTop: '0.25rem' }}
					>
						<Icon fontSize="small">
							{draft.cleared ? 'restore' : 'backspace'}
						</Icon>
					</IconButton>
				</Tooltip>
			)}
		</FlexLayout>
	);
};

/* ------------------------------------------------------------------ */
/* Card shell                                                          */
/* ------------------------------------------------------------------ */

type ProviderCardProps = {
	title: string;
	subtitle?: string;
	enabled: boolean;
	onEnabledChange: (enabled: boolean) => void;
	onSave: () => void;
	saving?: boolean;
	/** Hard-disables the whole card (switch, fields, save) with a reason line. */
	disabled?: boolean;
	disabledReason?: string;
};

export const ProviderCard: FC<ProviderCardProps> = ({
	title,
	subtitle,
	enabled,
	onEnabledChange,
	onSave,
	saving,
	disabled,
	disabledReason,
	children,
}) => {
	return (
		<CardShell>
			<FlexLayout justifyContent="space-between" alignItems="center">
				<div>
					<Typography variant="h6">{title}</Typography>
					{subtitle && (
						<Typography variant="caption" color="textSecondary">
							{subtitle}
						</Typography>
					)}
				</div>
				<FlexLayout alignItems="center" gridGap="0.25rem">
					<Typography variant="caption" color="textSecondary">
						{enabled && !disabled ? 'Enabled' : 'Disabled'}
					</Typography>
					<Switch
						checked={enabled && !disabled}
						disabled={disabled}
						onChange={(event) => onEnabledChange(event.target.checked)}
					/>
				</FlexLayout>
			</FlexLayout>
			{disabled && disabledReason && (
				<DisabledReason variant="caption">{disabledReason}</DisabledReason>
			)}
			{/* fieldset[disabled] blocks every input inside without touching
			    each child; the opacity signals the state at a glance. */}
			<DisableableFields disabled={disabled}>
				<CardFields>{children}</CardFields>
			</DisableableFields>
			<FlexLayout justifyContent="flex-end">
				<EdButton
					variant="contained"
					edcolor="purple"
					disabled={saving || disabled}
					onClick={onSave}
				>
					Save
				</EdButton>
			</FlexLayout>
		</CardShell>
	);
};

const DisableableFields = styled.fieldset`
	border: 0;
	margin: 0;
	padding: 0;
	min-width: 0;

	&[disabled] {
		opacity: 0.5;
		pointer-events: none;
	}
`;

const DisabledReason = styled(Typography)`
	color: ${(props) => props.theme.palette?.warning?.main ?? '#b26a00'};
	display: block;
`;

export const CardSectionTitle = styled(Typography).attrs({
	variant: 'subtitle2',
})`
	margin-top: 0.5rem;
`;

export const CardNote = styled(Typography).attrs({ variant: 'caption' })`
	color: rgba(0, 0, 0, 0.6);
	display: block;
`;

const CardShell = styled(Paper)`
	padding: 1.25rem 1.5rem;
	display: flex;
	flex-direction: column;
	gap: 1rem;
`;

const CardFields = styled.div`
	display: flex;
	flex-direction: column;
	gap: 1.25rem;
`;
